import { Description, NoteAdd, TaskAlt } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { Datagrid, FunctionField, List, TextField, useRecordContext, useTranslate } from "react-admin";

import { ROWS_PER_PAGE } from "../constants";
import { Activity } from "../lib/activityUtils";
import { PatientRecord } from "../types/patient";
import { ActivityTypeField } from "./ActivityTypeField";
import { ContentTitleField } from "./ContentTitleField";
import { LocalizedDateField } from "./fields/LocalizedDateField";
import { ParticipantsField } from "./ParticipantsField";
import { PractitionerRecord } from "./PractitionerShow";
import { SessionDocumentationDialog } from "./SessionDocumentationDialog";

type ActivityListProps = {
  resource: "PractitionerActivities" | "PatientActivities";
};

export const ActivityList = ({ resource }: ActivityListProps) => {
  const record = useRecordContext<PractitionerRecord | PatientRecord>();
  const translate = useTranslate();
  const [docActivity, setDocActivity] = useState<Activity | null>(null);

  if (!record) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("activityList.noDataAvailable")}
      </Typography>
    );
  }

  const emptyMessage =
    resource === "PatientActivities"
      ? translate("activityList.noActivitiesPatient")
      : translate("activityList.noActivitiesPractitioner");

  return (
    <List
      resource={resource}
      filter={{ userId: record.userId }}
      actions={false}
      perPage={ROWS_PER_PAGE}
      title={translate("activityList.title")}
      empty={<Typography variant="body1">{emptyMessage}</Typography>}
      sort={{ field: "beganAt", order: "DESC" }}
    >
      <Datagrid bulkActionButtons={false} rowClick={false}>
        {resource === "PractitionerActivities" && <ParticipantsField label={translate("activityList.participants")} />}
        <ContentTitleField label={translate("activityList.content")} />
        <ActivityTypeField label={translate("activityList.type")} />
        <TextField source="duration" textAlign="left" label={translate("activityList.duration")} />
        <LocalizedDateField
          source="beganAt"
          label={translate("activityList.dateTime")}
          showTime
          options={{
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }}
        />
        <FunctionField
          label={translate("activityList.documentation")}
          render={(activity: Activity) => {
            const hasNote = !!activity.notes?.length;
            const isDocumented = !!activity.notes?.some(note => !!note.publishedAt);
            const Icon = isDocumented ? TaskAlt : hasNote ? Description : NoteAdd;

            return (
              <IconButton
                size="small"
                onClick={() => setDocActivity(activity)}
                color={isDocumented ? "default" : "primary"}
                aria-label={`${translate("sessionDocumentation.open")} — ${translate(
                  isDocumented ? "sessionDocumentation.documented" : "sessionDocumentation.notDocumented"
                )}`}
                title={translate(
                  isDocumented ? "sessionDocumentation.documented" : "sessionDocumentation.notDocumented"
                )}
              >
                <Icon />
              </IconButton>
            );
          }}
        />
      </Datagrid>
      {docActivity && (
        <SessionDocumentationDialog
          activity={docActivity}
          seniorId={resource === "PatientActivities" ? record.userId : undefined}
          onClose={() => setDocActivity(null)}
        />
      )}
    </List>
  );
};
